import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AppLayout } from '../../components/layout/AppLayout';
import { Button } from '../../components/ui/Button';
import { useToast } from '../../components/ui/ToastProvider';
import { api, errorMessage } from '../../lib/api';
import { useAsync } from '../../lib/hooks';
import { addDaysISO, todayISO } from '../../lib/format';
import { mailSummaryMessage } from '../../lib/mail';
import type {
  CreateRfpPayload,
  ExtractionResult,
  MailDispatchSummary,
  Paginated,
  Product,
  ProductCategory,
  RfpDocument,
  Vendor,
} from '../../types/api';
import { WizardStepper } from './components/WizardStepper';
import { UploadQuotationStep } from './components/UploadQuotationStep';
import { ReviewDetailsStep } from './components/ReviewDetailsStep';

const STEPS = ['Upload Quotation', 'Extract & Review Details'] as const;

/**
 * Screens RFP 02/03 (step 1 - Upload Quotation) and RFP 04/06 (step 2 - Extract
 * & Review Details), reached from the "+ Add RFP" button on the list screen.
 */
export function AddRfpPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [step, setStep] = useState(0);
  const [document, setDocument] = useState<RfpDocument | null>(null);
  const [uploading, setUploading] = useState(false);
  const [extraction, setExtraction] = useState<ExtractionResult | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);

  const [categoryId, setCategoryId] = useState<number | ''>('');
  const [selectedProductIds, setSelectedProductIds] = useState<Set<number>>(new Set());
  const [selectedVendorIds, setSelectedVendorIds] = useState<Set<number>>(new Set());

  const [title, setTitle] = useState('');
  const [dueDate, setDueDate] = useState(addDaysISO(30));
  const [notes, setNotes] = useState('');

  const categoriesLoader = useCallback(
    () => api.get<Paginated<ProductCategory>>('/product-categories', { perPage: 100 }),
    [],
  );
  const { data: categoryPage } = useAsync(categoriesLoader, []);
  const categories = useMemo(() => categoryPage?.items ?? [], [categoryPage]);

  // Ordered by vendor code so the first rows match the design's vendor table.
  const vendorsLoader = useCallback(
    () =>
      api.get<Paginated<Vendor>>('/vendors', {
        perPage: 100,
        isActive: true,
        sortBy: 'vendor_code',
        sortOrder: 'asc',
      }),
    [],
  );
  const { data: vendorPage } = useAsync(vendorsLoader, []);
  const vendors = useMemo(() => vendorPage?.items ?? [], [vendorPage]);

  const productsLoader = useCallback(
    () => api.get<Paginated<Product>>('/products', { categoryId: categoryId || undefined, perPage: 100 }),
    [categoryId],
  );
  const { data: productPage, loading: productsLoading } = useAsync(productsLoader, [categoryId]);
  const products = useMemo(() => productPage?.items ?? [], [productPage]);

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const created = await api.upload<RfpDocument>('/documents/upload', file, {
        documentType: 'quotation',
      });
      setDocument(created);
      showToast(`${created.originalName} uploaded.`, 'success');
    } catch (cause) {
      showToast(errorMessage(cause), 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveDocument = async () => {
    if (!document) return;
    try {
      await api.delete(`/documents/${document.id}`);
    } catch {
      // Deleting is best-effort; the row may already be gone.
    }
    setDocument(null);
    setExtraction(null);
    setStep(0);
  };

  const goToReviewStep = async () => {
    if (!document) return;
    setExtracting(true);
    try {
      const result = await api.post<ExtractionResult>(`/documents/${document.id}/extract`);
      setExtraction(result);
      setCategoryId(result.productInformation.productCategoryId ?? '');
      setSelectedProductIds(new Set(result.productInformation.products.map((product) => product.id)));
      setSelectedVendorIds(new Set(result.suggestedVendors.map((vendor) => vendor.id)));
      setTitle((current) => current || result.productInformation.title);
      setNotes((current) => current || result.productInformation.notes);
      setStep(1);
    } catch (cause) {
      showToast(errorMessage(cause), 'error');
    } finally {
      setExtracting(false);
    }
  };

  const toggle = (setter: React.Dispatch<React.SetStateAction<Set<number>>>, id: number) => {
    setter((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSave = async (sendToVendors: boolean) => {
    setSaving(true);
    try {
      const payload: CreateRfpPayload = {
        title: title || undefined,
        productCategoryId: categoryId === '' ? undefined : categoryId,
        createdOn: todayISO(),
        dueDate: dueDate || undefined,
        notes: notes || undefined,
        // `createdBy` is taken from the access token by the API.
        documentId: document?.id,
        productIds: [...selectedProductIds],
        vendorIds: [...selectedVendorIds],
        status: sendToVendors ? 'send_to_vendors' : 'draft',
        sendToVendors,
      };

      const created = await api.post<{ id: number; rfpNumber: string; mail?: MailDispatchSummary }>(
        '/rfps',
        payload,
      );
      const mailNote = mailSummaryMessage(created.mail);

      navigate('/rfp', {
        state: {
          toast: sendToVendors
            ? ['RFP created and sent to the selected vendors.', mailNote]
                .filter(Boolean)
                .join(' ')
            : `${created.rfpNumber} saved as a draft.`,
        },
      });
    } catch (cause) {
      showToast(errorMessage(cause), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppLayout title="RFP">
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <Link to="/rfp" className="breadcrumb__link">
          RFP
        </Link>
        <span aria-hidden="true">›</span>
        <span className="breadcrumb__current">Add New RFP</span>
      </nav>

      <div className="page-header">
        <h2 className="page-header__title">Add New RFP</h2>

        <div className="page-header__actions">
          <Button variant="outlined-brand" onClick={() => navigate('/rfp')} disabled={saving}>
            Cancel
          </Button>
          <Button variant="outlined" onClick={() => handleSave(false)} disabled={saving}>
            {saving ? 'Saving…' : 'Save Draft'}
          </Button>

          {step === 0 ? (
            <Button
              variant="primary"
              onClick={goToReviewStep}
              disabled={!document || extracting}
            >
              {extracting ? 'Extracting…' : 'Next'}
            </Button>
          ) : (
            <Button variant="primary" onClick={() => handleSave(true)} disabled={saving}>
              Save &amp; Send to Vendors
            </Button>
          )}
        </div>
      </div>

      <section className="panel panel--flush">
        <WizardStepper steps={STEPS} currentStep={step} />
      </section>

      <div className="wizard-content">
        {step === 0 ? (
          <UploadQuotationStep
            document={document}
            uploading={uploading}
            onSelectFile={handleUpload}
            onRemove={handleRemoveDocument}
          />
        ) : extraction ? (
          <ReviewDetailsStep
            extraction={extraction}
            categories={categories}
            products={products}
            productsLoading={productsLoading}
            categoryId={categoryId}
            onCategoryChange={(value) => {
              setCategoryId(value);
              setSelectedProductIds(new Set());
            }}
            selectedProductIds={selectedProductIds}
            onToggleProduct={(productId) => toggle(setSelectedProductIds, productId)}
            vendors={vendors}
            selectedVendorIds={selectedVendorIds}
            onToggleVendor={(vendorId) => toggle(setSelectedVendorIds, vendorId)}
            onToggleAllVendors={(checked) =>
              setSelectedVendorIds(checked ? new Set(vendors.map((vendor) => vendor.id)) : new Set())
            }
            title={title}
            onTitleChange={setTitle}
            dueDate={dueDate}
            onDueDateChange={setDueDate}
            notes={notes}
            onNotesChange={setNotes}
          />
        ) : null}
      </div>
    </AppLayout>
  );
}
